import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateFormPacket } from 'api/FormPacket';

import { ia } from 'lib/helpers/utility';
import { usePacketBuilderContext } from 'lib/context/PacketBuilder/PacketBuilderContext';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';
import ErrorBoundary, { withErrorBoundary } from 'components/shared/Error/Boundary';

const editInitialBreadcrumbs = [
  {
    title: 'Packet name & automation',
    view: 'name_and_automation',
    active: true,
    isFinished: false
  },
  { title: 'Providers', view: 'providers', active: false, isFinished: false },
  { title: 'Services', view: 'services', active: false, isFinished: false },
  { title: 'Tags', view: 'tags', active: false, isFinished: false },
  { title: 'Forms', view: 'forms', active: false, isFinished: false }
];

const Actions = ({ data }) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { formik, setIsEdit, setPacket, setBreadCrumbs, setOpenModal } = usePacketBuilderContext();

  const { mutateAsync: mutateDeleteFormPacket } = useMutation({
    mutationFn: (data) => updateFormPacket(navigate, data)
  });

  const handleEdit = () => {
    setIsEdit(true);
    setBreadCrumbs(editInitialBreadcrumbs);
    setPacket({
      ...data
    });
    formik.setValues({
      ...formik.values,
      ...data,
      tag_ids: data.tag_ids.map((tag) => tag.value),
      provider_ids: data.provider_ids.map((provider) => provider.value),
      service_ids: data.service_ids.map((service) => service.value),
      form_ids: data.form_ids.map((form) => form.value),
      automation: {
        ...formik.values.automation,
        tags: ia(data.tag_ids),
        providers: ia(data.provider_ids),
        services: ia(data.service_ids),
        forms: ia(data.form_ids),
        patient_creation: data?.patient_creation
      }
    });
    setOpenModal(true);
  };

  const handleDelete = async () => {
    const variables = { fields: { status: 'deleted' }, formPacketId: data?.id };
    await mutateDeleteFormPacket(variables, {
      onSuccess: ({ code }) => {
        if (code === 0) {
          queryClient.invalidateQueries(['form_packets']);
          showAlert({ title: 'Packet deleted', color: 'success' });
        } else {
          showAlert({ title: "Couldn't delete packet", color: 'danger' });
        }
        setShowConfirmationModal(false);
      }
    });
  };

  const options = [
    { label: 'Edit', onClick: handleEdit, icon: 'edit-2-mono' },
    { label: 'Delete', onClick: () => setShowConfirmationModal(true), icon: 'trash-mono' }
  ];
  return (
    <ErrorBoundary FallbackComponent={ErrorMessage}>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={options}
        isBorder={false}
      />
      <Confirm
        variant="danger"
        title="Delete packet"
        icon="trash-mono"
        message="Are you sure you want to delete this packet?"
        isOpen={!!showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        footerActions={{
          primary: {
            text: 'Delete',
            onClick: handleDelete,
            color: 'danger'
          }
        }}
      />
    </ErrorBoundary>
  );
};

export default withErrorBoundary(Actions);
